import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Helmet from 'react-helmet';
import TextField from '@material-ui/core/TextField';
import { Button, Typography, LinearProgress } from '@material-ui/core';

import { selectUser } from 'entities/user/selectors';
import Box from 'retired/Box';
import OrganizationMenu from 'retired/OrganizationMenu';
import { useInjectEpics } from 'utils/injectEpics';
import { useInjectReducer } from 'utils/injectReducer';
import { showErrorAction } from 'containers/Notification/actions';

import { Privileges } from '../../utils/privileges';

import epics from './epics';
import reducer from './reducer';
import { submitAction } from './types';
import { selectSubmitting } from './selectors';

const NonprofitsImportPage = () => {
  const dispatch = useDispatch();
  useInjectEpics({ key: 'nonprofitsImportPage', epics });
  useInjectReducer({ key: 'nonprofitsImportPage', reducer });

  const submitting = useSelector(selectSubmitting);
  const user = useSelector(selectUser);

  const [data, setData] = useState('');

  const submitData = (): void => {
    const PANs = data
      .split(/\r?\n/)
      .map((line) => line.replace(/\s/g, '').toUpperCase())
      .filter(Boolean);

    const invalidPANs = PANs.filter((PAN) => !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(PAN));

    if (invalidPANs.length > 0) {
      dispatch(
        showErrorAction(`These values are not valid PAN numbers: ${invalidPANs.join(', ')}`)
      );
      return;
    }
    // Protection against submitting incomplete list of nonprofits
    if (PANs.length < 100) {
      dispatch(
        showErrorAction(
          `You are supposed to enter the entire list of active GiveIndia nonprofits. It's not like there are less than 100 of them, right?`
        )
      );
      return;
    }
    dispatch(submitAction({ nonprofitIds: PANs.map((PAN) => `ext-gi-${PAN}`) }));
  };

  return (
    <>
      {(user?.hasPrivilege(Privileges.ORGANIZATION_MANAGE_SETTINGS) ||
        user?.hasPrivilege(Privileges.ORGANIZATION_VIEW_ORGANIZERS)) && (
        <div style={{ marginBottom: 60 }}>
          <OrganizationMenu user={user} />
        </div>
      )}
      {submitting && (
        <div style={{ maxWidth: 1200 }}>
          <LinearProgress />
        </div>
      )}
      <Box style={{ maxWidth: 1200 }}>
        <Helmet title="User Settings" />
        <h2>Import GiveIndia Nonprofits</h2>
        <p>
          Copy the whole column with PAN numbers from the GiveIndia export file and paste it here.
          GiveIndia nonprofits that are not among these will be disabled from receiving donations.
        </p>
        <TextField
          multiline
          placeholder="AAAAA9999A"
          fullWidth
          label="PAN-numbers"
          helperText="PAN-numbers, each on separate line"
          rows={20}
          onChange={(text) => setData(text.target.value)}
        />

        <Typography align="right">
          <Button
            color="primary"
            onClick={submitData}
            disabled={submitting}
            variant="contained"
            style={{ marginTop: 30 }}
          >
            Submit
          </Button>
        </Typography>
      </Box>
    </>
  );
};

export default NonprofitsImportPage;
